import type { Card, CardTransaction } from '../cards.types';
import { formatCardDateTime, formatSignedAmount } from './format';
import { buildStatementPdf } from './statement-pdf';
import { buildStatementXlsx } from './statement-xlsx';

export type StatementFileType = 'pdf' | 'csv' | 'xls';

const FILE_EXTENSIONS: Record<StatementFileType, string> = {
  pdf: 'pdf',
  csv: 'csv',
  xls: 'xlsx',
};

const HEADER = ['Date', 'Counterparty', 'Status', 'Amount'];

const signedAmount = (transaction: CardTransaction) =>
  transaction.direction === 'in' ? transaction.amount : -transaction.amount;

const escapeCsv = (value: string) => `"${value.replace(/"/g, '""')}"`;

const buildCsv = (transactions: CardTransaction[]): Blob => {
  const rows = transactions.map((transaction) => [
    formatCardDateTime(transaction.occurredAt),
    transaction.merchant.name,
    transaction.status,
    signedAmount(transaction).toFixed(2),
  ]);
  const content = [HEADER, ...rows].map((row) => row.map(escapeCsv).join(',')).join('\n');

  return new Blob([content], { type: 'text/csv;charset=utf-8' });
};

const buildPdf = (card: Card, transactions: CardTransaction[]): Blob =>
  buildStatementPdf({
    title: `Card statement — ${card.name} •••• ${card.last4}`,
    subtitle: `${card.holderName} · ${card.currency} · generated ${formatCardDateTime(new Date().toISOString())}`,
    columns: [{ x: 40 }, { x: 150 }, { x: 370 }, { x: 555, align: 'right' }],
    header: HEADER,
    rows: transactions.map((transaction) => [
      formatCardDateTime(transaction.occurredAt),
      transaction.merchant.name,
      transaction.status,
      formatSignedAmount(transaction.amount, transaction.direction, card.currency),
    ]),
  });

const buildXlsx = (card: Card, transactions: CardTransaction[]): Blob =>
  buildStatementXlsx(`${card.name} ${card.last4}`, [
    HEADER,
    ...transactions.map((transaction) => [
      formatCardDateTime(transaction.occurredAt),
      transaction.merchant.name,
      transaction.status,
      signedAmount(transaction),
    ]),
  ]);

export const buildStatement = (
  card: Card,
  transactions: CardTransaction[],
  fileType: StatementFileType,
): Blob => {
  switch (fileType) {
    case 'pdf':
      return buildPdf(card, transactions);
    case 'csv':
      return buildCsv(transactions);
    case 'xls':
      return buildXlsx(card, transactions);
  }
};

export const statementFileName = (card: Card, fileType: StatementFileType) =>
  `${card.name.toLowerCase().replace(/\s+/g, '-')}-${card.last4}-statement.${FILE_EXTENSIONS[fileType]}`;
